import React, { ReactNode, useEffect, useMemo, useState } from "react";
import CaTable from "../table/CaTable";
import CaTableHeader from "../table/CaTableHeader";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import CaButton from "../button/CaButton";
import CaTableRow from "../table/CaTableRow";
import CaTableHead from "../table/CaTableHead";
import CaTableBody from "../table/CaTableBody";
import CaTableCell from "../table/CaTableCell";
import CaTableCaption from "../table/CaTableCaption";
import CaInput from "../input/CaInput";
import { FilterIcon } from "@/icons/primary/filter-icon";

export type CaDataTableProps = {
  title?: string | React.ReactNode;
  data: any[];
  columns: ColumnDef<any>[];
  hideSearchBar?: boolean;
  filters?: (table: ReturnType<typeof useReactTable>) => React.ReactNode;
  preventRowClickColumnIds?: string[];
  onRowClick?: (row: any, cell: any, e: any) => void;
  onRowSelectionChange?: (rows: any) => void;
  onPaginationChange?: (pagination: { pageIndex: number; pageSize: number }) => void;
  onSearchChange?: (search: string) => void;
  onSortingChange?: (sorting: SortingState) => void;
  manualSorting?: boolean;
  enableControlledPagination?: boolean;
  pageCount?: number;
  perPage?: number;
  bottomActions?: React.ReactNode;
  withoutShadow?: boolean;
  actions?: ReactNode;
};

const DEFAULT_PER_PAGE = 10;

function CaDataTable({
  title,
  data,
  columns,
  hideSearchBar,
  filters,
  preventRowClickColumnIds,
  onRowClick,
  onRowSelectionChange,
  onPaginationChange,
  onSearchChange,
  onSortingChange,
  manualSorting,
  enableControlledPagination,
  pageCount,
  perPage,
  bottomActions,
  withoutShadow,
  actions,
}: CaDataTableProps) {
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<{
    [key: number]: boolean;
  }>({});
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: perPage || DEFAULT_PER_PAGE,
  });

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  );

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (enableControlledPagination) {
      setPagination({ pageIndex: 0, pageSize });
      onPaginationChange?.({ pageIndex: 0, pageSize });
    }
    const value = String(event.target.value);
    setSearch(value);
    onSearchChange?.(value);
  };

  const table = useReactTable({
    data,
    columns,
    enableGlobalFilter: true,
    onSortingChange: (tempSorting) => {
      onSortingChange && typeof tempSorting === "function" && onSortingChange(tempSorting(sorting));
      setSorting(tempSorting);
    },
    onGlobalFilterChange: setSearch,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    pageCount: enableControlledPagination ? pageCount : undefined,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter: search,
      pagination,
    },
    onPaginationChange: setPagination,
    debugTable: false,
    manualPagination: enableControlledPagination,
    ...(manualSorting ? { manualSorting: true } : {}),
  });

  useEffect(() => {
    if (enableControlledPagination) {
      onPaginationChange?.({ pageIndex, pageSize });
    }
  }, [pageIndex, pageSize]);

  useEffect(() => {
    const indexes = Object.keys(rowSelection).filter((key: any) => rowSelection[key]);
    const selectedRows = indexes.map((index) => data[Number(index)]);
    onRowSelectionChange?.(selectedRows);
  }, [rowSelection]);

  return (
    <div className='w-full'>
      {(title || filters || !hideSearchBar) && (
        <div className='flex justify-between items-center gap-3 py-4 w-full'>
          {typeof title === "string" ? (
            <h3 className='font-sans-serif text-lg text-deep-blue'>{title}</h3>
          ) : (
            title
          )}
          <div className='flex justify-end items-center gap-2'>
            {!hideSearchBar && (
              <CaInput
                placeholder='Search...'
                value={search}
                onChange={handleSearch}
                className='max-w-64'
                elementSize='small'
                rounded
              />
            )}
            {filters && (
              <CaButton
                leftIcon={<FilterIcon className='text-lg' />}
                size='small'
                minimal
                onClick={() => setShowFilters(!showFilters)}
              />
            )}
            {actions}
          </div>
        </div>
      )}
      <div className=''>
        <CaTable withoutShadow={!!withoutShadow}>
          {showFilters && filters && (
            <CaTableCaption className='caption-top'>{filters?.(table)}</CaTableCaption>
          )}
          <CaTableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <CaTableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <CaTableHead key={header.id} className='!py-3.5'>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </CaTableHead>
                  );
                })}
              </CaTableRow>
            ))}
          </CaTableHeader>
          <CaTableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <CaTableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                  {row.getVisibleCells().map((cell) => (
                    <CaTableCell
                      key={cell.id}
                      className={
                        onRowClick && !preventRowClickColumnIds?.includes(cell.column.id)
                          ? "cursor-pointer"
                          : ""
                      }
                      onClick={(e: any) => {
                        if (preventRowClickColumnIds?.includes(cell.column.id)) {
                          return;
                        }
                        onRowClick?.(row, cell, e);
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </CaTableCell>
                  ))}
                </CaTableRow>
              ))
            ) : (
              <CaTableRow>
                <CaTableCell colSpan={columns.length} className='h-24 text-center'>
                  No results.
                </CaTableCell>
              </CaTableRow>
            )}
          </CaTableBody>
          <CaTableCaption className='caption-bottom'>
            <div className='flex items-center justify-between gap-2'>
              <div>{bottomActions}</div>
              <div className='flex items-center gap-4'>
                {table.getCanPreviousPage() && (
                  <CaButton
                    size='small'
                    outlined
                    intent='secondary'
                    onClick={() => table.previousPage()}
                  >
                    Previous
                  </CaButton>
                )}
                <div className='text-sm'>
                  {table.getState().pagination.pageIndex + 1}
                  {(pageCount && pageCount > 0) || !enableControlledPagination
                    ? `/${table.getPageCount()}`
                    : ""}
                </div>
                <CaButton
                  size='small'
                  outlined
                  intent='secondary'
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  Next
                </CaButton>
              </div>
            </div>
          </CaTableCaption>
        </CaTable>
      </div>
    </div>
  );
}

CaDataTable.defaultProps = {
  title: undefined,
  filters: undefined,
  onRowClick: undefined,
  enableControlledPagination: false,
  pageCount: undefined,
  perPage: DEFAULT_PER_PAGE,
  onRowSelectionChange: undefined,
  onPaginationChange: undefined,
  onSearchChange: undefined,
  hideSearchBar: false,
};

export default CaDataTable;
